import axios from 'axios'
import logBuildError from '@helpers/misc/logBuildError'

const handleProductsBuildError = logBuildError('reviews')

type Rating = {
  average_score: number
  total_review: number
  star_distribution?: Record<string, number>
  group?: string
}

type YotpoProduct = {
  average_score: number
  total_reviews: number
  products_group?: {
    display_name: string
  }
}

const getYotpoProducts = (count: number, page = 1) => {
  return axios.get(
    `https://api.yotpo.com/v1/apps/${process.env.YOTPO_APP_KEY_GB}/products?utoken=${process.env.YOTPO_TOKEN_GB}&count=${count}&page=${page}`
  )
}

const getRatingsFromProducts = (products: YotpoProduct[]) => {
  const ratings: Rating[] = products.map(product => {
    return {
      average_score: product.average_score,
      total_review: product.total_reviews,
      group: product.products_group?.display_name
    }
  })

  return ratings
}

export const getRatingsByProductGroup = async () => {
  const reviewsPerPage = 100
  const firstPage = await getYotpoProducts(reviewsPerPage, 1)

  if (!firstPage?.data?.pagination) {
    return {}
  }

  const totalPages =
    process.env.ENVIRONMENT === 'development'
      ? 1
      : Math.ceil(firstPage.data.pagination.total / reviewsPerPage)
  const groupRatings: Record<string, Rating> = {}
  const promises = []

  for (let page = 1; page <= totalPages; page++) {
    promises.push(getYotpoProducts(reviewsPerPage, page))
  }

  const allPages = await Promise.all(promises)

  allPages.forEach(page => {
    if (!page?.data?.products) return

    const ratedProducts = getRatingsFromProducts(page.data.products)

    ratedProducts.forEach(product => {
      const group = product.group
      if (!group) return

      const existingRating = groupRatings[group]

      if (
        !existingRating ||
        existingRating?.total_review < product.total_review
      ) {
        const starDistribution: Record<string, number> = {
          '1': 0,
          '2': 0,
          '3': 0,
          '4': 0,
          '5': 0
        }
        const seletedRating = Math.floor(product.average_score).toString()
        starDistribution[seletedRating] = product.total_review

        groupRatings[group] = {
          ...product,
          star_distribution: starDistribution
        }
      }
    })
  })

  return groupRatings
}

export const yotpoRatingsHandler = () => {
  let groupRatings: Record<string, Rating>

  return {
    getByGroup: async function () {
      if (!groupRatings) {
        try {
          groupRatings = await getRatingsByProductGroup()
        } catch (e) {
          handleProductsBuildError({
            errorLevel: 'warning',
            properties: {
              message: `Unable to fetch product reviews`
            }
          })

          return {}
        }
      }

      return groupRatings
    }
  }
}

export const formatGroupType = (type: string) => {
  return type
    .toLowerCase()
    .replace(' - ', '_')
    .replace(/\s/g, '_')
    .replace(/_legacy|_tapered/g, '')
}
